import { useMutation } from "@tanstack/react-query";
import { isValid } from "date-fns";
import { Archive, ArchiveRestore } from "lucide-react";
import {
  InfiniteListBase,
  ShowBase,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useTranslate,
  useUpdate,
} from "ra-core";
import { DeleteButton } from "@/components/admin/delete-button";
import { DataTable } from "@/components/admin/data-table";
import { NumberField } from "@/components/admin/number-field";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceArrayField } from "@/components/admin/reference-array-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { NoteCreate } from "../notes/NoteCreate";
import { NotesIterator } from "../notes/NotesIterator";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contract, Deal } from "../types";
import { ContactList } from "./ContactList";
import {
  isDealSnoozed,
  SnoozeButton,
  UnsnoozeButton,
} from "./DealSnoozeControls";
import { format } from "date-fns";
import { findDealLabel, formatISODateString } from "./dealUtils";
import { Link } from "react-router";
import { ContractStatusBadge } from "../contracts/ContractStatusBadge";

export const DealShow = ({ open, id }: { open: boolean; id?: string }) => {
  const redirect = useRedirect();
  const handleClose = () => {
    redirect("list", "deals");
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:max-w-lg lg:max-w-4xl p-4 overflow-y-auto overflow-x-hidden max-h-[calc(100dvh-1rem)] sm:max-h-9/10 top-2 translate-y-0 sm:top-1/20">
        {id ? (
          <ShowBase id={id}>
            <DealShowContent />
          </ShowBase>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const DealShowContent = () => {
  const translate = useTranslate();
  const { dealStages, dealCategories, currency } = useConfigurationContext();
  const record = useRecordContext<Deal>();
  if (!record) return null;

  return (
    <>
      <div className="space-y-2">
        {record.archived_at ? <ArchivedTitle /> : null}
        {!record.archived_at && isDealSnoozed(record) ? (
          <SnoozedTitle until={record.snoozed_until as string} />
        ) : null}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-6 sm:mb-8">
            <div className="flex min-w-0 items-center gap-3 sm:gap-4 pr-10 sm:pr-0">
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
              >
                <CompanyAvatar />
              </ReferenceField>
              <h2 className="min-w-0 break-words text-xl sm:text-2xl font-semibold">
                {record.name}
              </h2>
            </div>
            <div
              className={`flex flex-wrap gap-2 ${record.archived_at ? "" : "sm:pr-12"}`}
            >
              {record.archived_at ? (
                <>
                  <UnarchiveButton record={record} />
                  <DeleteButton />
                </>
              ) : (
                <>
                  {isDealSnoozed(record) ? (
                    <UnsnoozeButton deal={record} />
                  ) : (
                    <SnoozeButton deal={record} />
                  )}
                  <ArchiveButton record={record} />
                  {record.stage === "won" ? (
                    <Button asChild>
                      <Link to={`/contracts/create?deal_id=${record.id}`}>
                        {translate(
                          "resources.contracts.action.create_from_deal",
                        )}
                      </Link>
                    </Button>
                  ) : null}
                  <EditButton />
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 m-0 sm:m-4">
            <div className="flex min-w-0 flex-col">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.expected_closing_date")}
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm">
                  {isValid(new Date(record.expected_closing_date))
                    ? formatISODateString(record.expected_closing_date)
                    : translate("resources.deals.invalid_date")}
                </span>
                {new Date(record.expected_closing_date) < new Date() ? (
                  <Badge variant="destructive">
                    {translate("crm.common.past")}
                  </Badge>
                ) : null}
              </div>
            </div>

            <div className="flex min-w-0 flex-col">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.amount")}
              </span>
              <span className="text-sm">
                {record.amount.toLocaleString("en-US", {
                  notation: "compact",
                  style: "currency",
                  currency,
                  currencyDisplay: "narrowSymbol",
                  minimumSignificantDigits: 3,
                })}
              </span>
            </div>

            {record.category && (
              <div className="flex min-w-0 flex-col">
                <span className="text-xs text-muted-foreground tracking-wide">
                  {translate("resources.deals.fields.category")}
                </span>
                <span className="break-words text-sm">
                  {dealCategories.find((c) => c.value === record.category)
                    ?.label ?? record.category}
                </span>
              </div>
            )}

            <div className="flex min-w-0 flex-col">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.stage")}
              </span>
              <span className="break-words text-sm">
                {findDealLabel(dealStages, record.stage)}
              </span>
            </div>
          </div>

          {!!record.contact_ids?.length && (
            <div className="my-4 sm:m-4">
              <div className="flex min-h-12 min-w-0 flex-col">
                <span className="text-xs text-muted-foreground tracking-wide">
                  {translate("resources.deals.fields.contact_ids")}
                </span>
                <ReferenceArrayField
                  source="contact_ids"
                  reference="contacts_summary"
                >
                  <ContactList />
                </ReferenceArrayField>
              </div>
            </div>
          )}

          {record.description && (
            <div className="my-4 sm:m-4 whitespace-pre-line">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.description")}
              </span>
              <p className="break-words text-sm leading-6">
                {record.description}
              </p>
            </div>
          )}

          <div className="my-4 sm:m-4">
            <Separator className="mb-4" />
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-base font-medium">
                {translate("resources.contracts.name", { smart_count: 2 })}
              </h3>
              {record.stage === "won" ? (
                <Button asChild size="sm" variant="secondary">
                  <Link to={`/contracts/create?deal_id=${record.id}`}>
                    {translate("resources.contracts.action.create_from_deal")}
                  </Link>
                </Button>
              ) : null}
            </div>
            <ReferenceManyField
              reference="contracts"
              target="deal_id"
              sort={{ field: "created_at", order: "DESC" }}
              empty={
                <p className="text-sm text-muted-foreground">
                  {translate("resources.contracts.empty.title")}
                </p>
              }
            >
              <DataTable<Contract> rowClick="show" bulkActionButtons={false}>
                <DataTable.Col source="title" />
                <DataTable.Col
                  source="status"
                  render={(contract) => (
                    <ContractStatusBadge status={contract.status} />
                  )}
                />
                <DataTable.Col source="total_amount">
                  <NumberField source="total_amount" />
                </DataTable.Col>
                <DataTable.Col source="next_payment_date" />
              </DataTable>
            </ReferenceManyField>
          </div>

          <div className="my-4 sm:m-4">
            <Separator className="mb-4" />
            <InfiniteListBase
              resource="deal_notes"
              filter={{ deal_id: record.id }}
              sort={{ field: "date", order: "DESC" }}
              perPage={25}
              disableSyncWithLocation
              storeKey={false}
              empty={<NoteCreate reference={"deals"} />}
            >
              <NotesIterator reference="deals" />
            </InfiniteListBase>
          </div>
        </div>
      </div>
    </>
  );
};

const ArchivedTitle = () => {
  const translate = useTranslate();
  return (
    <div className="bg-orange-500 px-6 py-4">
      <h3 className="text-lg font-bold text-white">
        {translate("resources.deals.archived.title")}
      </h3>
    </div>
  );
};

const SnoozedTitle = ({ until }: { until: string }) => {
  const translate = useTranslate();
  return (
    <div className="bg-amber-500 px-6 py-4">
      <h3 className="text-lg font-bold text-white">
        {translate("resources.deals.snoozed.title")}
        <span className="ml-2 font-normal text-sm">
          ({translate("resources.deals.snoozed.until_label")}{" "}
          {format(new Date(until), "dd.MM.yyyy")})
        </span>
      </h3>
    </div>
  );
};

const ArchiveButton = ({ record }: { record: Deal }) => {
  const translate = useTranslate();
  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleClick = () => {
    update(
      "deals",
      {
        id: record.id,
        data: { archived_at: new Date().toISOString() },
        previousData: record,
      },
      {
        onSuccess: () => {
          redirect("list", "deals");
          notify("resources.deals.archived.success", {
            type: "info",
            undoable: false,
          });
          refresh();
        },
        onError: () => {
          notify("resources.deals.archived.error", {
            type: "error",
          });
        },
      },
    );
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      className="flex items-center gap-2 h-9"
    >
      <Archive className="w-4 h-4" />
      {translate("resources.deals.archived.action")}
    </Button>
  );
};

const UnarchiveButton = ({ record }: { record: Deal }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();

  const { mutate } = useMutation({
    mutationFn: () => dataProvider.unarchiveDeal(record),
    onSuccess: () => {
      redirect("list", "deals");
      notify("resources.deals.unarchived.success", {
        type: "info",
        undoable: false,
      });
      refresh();
    },
    onError: () => {
      notify("resources.deals.unarchived.error", {
        type: "error",
      });
    },
  });

  const handleClick = () => {
    mutate();
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      className="flex items-center gap-2 h-9"
    >
      <ArchiveRestore className="w-4 h-4" />
      {translate("resources.deals.unarchived.action")}
    </Button>
  );
};
